<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";



// Services
import VehicleServiceRequest from '@/api/services/vehicleRequest.service';
import VehicleService from '@/api/services/vehicle.service';

// Icons Components
import IconVehicle from '@/components/icons/IconVehicle.vue';

// eslint-disable-next-line no-unused-vars
const uiStore = useUiStore();
// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'finish']);

const selectedVehicle = ref();
const dateAndTimeStart = ref();
const dateAndTimeEnd = ref();
const purpose = ref('')
const seatsNeeded = ref(false)
const observations = ref('')
const seatsCount = ref(1)
const startKm = ref()
const fuelType = ref();
const vehicleModel = ref();
const vehicleBrand = ref();
const maxSeats = ref();
const totalSeats = ref();
const today = ref(new Date());
const vehicleNeedsAdBlue = ref();
const vehicleHasAdblue = ref();
const defaultEntDate = computed(() => {
    if (dateAndTimeStart.value) {
        const d = new Date(dateAndTimeStart.value);
        d.setHours(15, 0, 0, 0);
        return d;
    }
    const d = new Date();
    d.setHours(15, 0, 0, 0);
    d.setDate(d.getDate() + 2);
    return d;
})

const fuelTypeOptions = ref([
    { label: 'Diesel', value: 'diesel' },
    { label: 'Gasolina', value: 'gasoline' },
    { label: 'Eléctrico', value: 'electric' },
    { label: 'Híbrido', value: 'hybrid' },
    { label: 'Híbrido enchufable', value: 'plugin_hybrid' },
]);

// eslint-disable-next-line no-unused-vars
const vehicles = ref([])

onMounted(() => {
    const d = new Date();
    d.setHours(8, 0, 0, 0)
    dateAndTimeStart.value = d;
    today.value.setHours(0, 0, 0, 0)
    getVehicles();
})


// Getter
const getVehicles = () => {
    let params = {
        page: 1,
        itemsPerPage: 9999,
        active: true,
    }

    uiStore.startLoading();
    VehicleService.getVehicles(params)
        .then((res) => {
            vehicles.value = res.data['hydra:member']
            // uiStore.setLoading(false);
            uiStore.stopLoading();
        })
        .catch(() => {
            Notification.addNotification('Error al intentar eliminar el empleado seleccionado.', 'error', 6000);
            // uiStore.setLoading(false);
            uiStore.stopLoading();
        })
}

const closeDialog = () => {
    emit('close');
}

const saveRequest = async () => {
    await resetErrors();
    await validate();
    if (Object.values(errors.value).some(Boolean)) {
        return;
    }
    let params = {
        vehicle: selectedVehicle.value,
        startDate: formatDate(new Date(dateAndTimeStart.value)),
        endDate: formatDate(new Date(dateAndTimeEnd.value)),
        purpose: purpose.value,
        startKm: startKm.value,
        requiresSeats: seatsNeeded.value,
        seatsCount: seatsCount.value,
        observations: observations.value,
    }
    VehicleServiceRequest.createVehicleRequest(params)
        .then((res) => {
            if (res && (res.status === 201 || res.status === 200)) {
                notification.addNotification({
                    type: 'success',
                    message: 'Solicitud creada',
                    duration: 6000
                })
                emit('finish');
                emit('close');
            }
        })

    // emit('finish')
    // emit('close')
}

// Validate
const resetErrors = () => {
    Object.keys(errors.value).forEach(key => {
        errors.value[key] = false
    })
}

const errors = ref({
    vehicle: false,
    startDate: false,
    endDate: false,
    purpose: false,
})

const validate = async () => {
    if (!selectedVehicle.value) errors.value.vehicle = true;
    if (!dateAndTimeStart.value) errors.value.startDate = true;
    if (!dateAndTimeEnd.value) errors.value.endDate = true;
    if (!purpose.value?.trim()) errors.value.purpose = true
    if (dateAndTimeStart.value && dateAndTimeEnd.value) {
        const start = new Date(dateAndTimeStart.value)
        const end = new Date(dateAndTimeEnd.value)
        if (end <= start) {
            errors.value.startDate = true
            errors.value.endDate = true
        }
    }
}

// Disabled dates
const isBeforeDateOnly = (current, start) => {
    const c = new Date(current.getFullYear(), current.getMonth(), current.getDate()).getTime();
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    return c < s;
}

const calculateDisabledDates = (time) => {
    if (dateAndTimeStart.value) {
        const start = new Date(dateAndTimeStart.value);
        const current = new Date(time);
        // Permitir el mismo día (sin importar hora)
        return isBeforeDateOnly(current, start);
    }
    return false;
}

const dueDatePickerOptions = ref({
    firstDayOfWeek: 1,
    disabledDate: calculateDisabledDates
});

const disabledDateFrom = (date) => {
    const current = new Date(date);
    if (!dateAndTimeStart.value) {
        return disabledDateAfterToday(current);
    }
    const start = new Date(dateAndTimeStart.value);
    return isBeforeDateOnly(current, start) || disabledDateAfterToday(current);
}
const disableDateTo = (date) => {
    if (!dateAndTimeEnd.value) {
        return disabledDateAfterToday(date);
    }

    const fechaHasta = new Date(dateAndTimeEnd.value);
    const dateToCheck = new Date(date);

    // no permitir después que fechaHasta ni después que hoy
    return dateToCheck > fechaHasta || disabledDateAfterToday(dateToCheck)
}

const disabledDateAfterToday = (date) => {
    return date.getTime() < today.value.getTime();
}

const manageChangeFrom = (val) => {
    // Si es nulo o inválido
    if (!val) {
        errors.value.startDate = true;
        return;
    }

    const fechaSeleccionada = new Date(val);
    if (fechaSeleccionada < new Date().setHours(0, 0, 0, 0)) {
        errors.value.startDate = true;
    } else {
        errors.value.startDate = false;

        // Si EndDate existe y es menor → resetear EndDate
        if (dateAndTimeEnd.value && fechaSeleccionada > new Date(dateAndTimeEnd.value)) {
            dateAndTimeEnd.value = null;
            errors.value.endDate = true;
        }
    }
}

const manageChangeTo = (val) => {
    if (!val) {
        errors.value.endDate = true;
        return;
    }

    const fechaSeleccionada = new Date(val);

    if (fechaSeleccionada <= new Date(today.value)) {
        errors.value.endDate = true;
    } else {
        errors.value.endDate = false;

        // Si Start existe y es mayor que End → resetear Start
        if (dateAndTimeStart.value && fechaSeleccionada < new Date(dateAndTimeStart.value)) {
            dateAndTimeEnd.value = null;
            errors.value.endDate = true;
        }
    }
}

const focusDateEnd = () => {
    if (dateAndTimeStart.value) {
        const start = new Date(dateAndTimeStart.value);
        const end = new Date(start);
        const LIMITE_HORA = 15;

        if (start.getHours() < LIMITE_HORA) {
            end.setHours(LIMITE_HORA, 0, 0, 0);
        } else {
            end.setHours(start.getHours() + 2, start.getMinutes(), 0, 0);
        }

        dateAndTimeEnd.value = end;
    }
}

const formatDate = (date) => {
    // Obtener el desplazamiento de la zona horaria
    const offset = new Date(today.value).getTimezoneOffset();
    const sign = offset > 0 ? '-' : '+';
    const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
    const minutes = String(Math.abs(offset) % 60).padStart(2, '0');

    // Formato de fecha
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    // Formato de hora
    const hoursOfDay = String(date.getHours()).padStart(2, '0');
    const minutesOfDay = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Devolver la cadena en formato ISO con el desplazamiento de la zona horaria
    return `${year}-${month}-${day}T${hoursOfDay}:${minutesOfDay}:${seconds}${sign}${hours}:${minutes}`;
}

const changeVehicle = () => {
    errors.value.vehicle = false
    let vt = vehicles.value.find(v => v['@id'] === selectedVehicle.value);
    if (vt && vt.currentKm && vt.currentKm > 0) {
        startKm.value = vt.currentKm;
    } else {
        startKm.value = 0;
    }
    if (vt) {
        fuelType.value = fuelTypeOptions.value.find(opt => opt.value === vt.fuelType)?.label;
        vehicleBrand.value = vt.brand;
        vehicleModel.value = vt.model;
        totalSeats.value = vt.totalSeats;
        vehicleHasAdblue.value = vt.requiresAdBlue
        vehicleNeedsAdBlue.value = vt.needsAdBlue;
        if (vt.maxSeats) {
            maxSeats.value = vt.maxSeats;
        } else {
            // maxSeats.value = vt.totalSeats;
            maxSeats.value = 9;
        }

    }
}

</script>

<template>
    <div class="vehicle-new-request-dialog">
        <div class="my-header">
            <div class="title-text ml-4">
                <IconVehicle class="iconVehicle" />
                Nueva Solicitud
            </div>
            <img class="close-icon pointer justify-content-end" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
                @click="closeDialog()">
        </div>
        <div class="row d-flex dialog-content">
            <div class="col-12 row d-flex mb-3">
                <div class="col-md-12 row d-flex">
                    <div class="col-md-6 el-col-xs-24">
                        <label class="w-100" for="car">Coche:</label>
                        <el-select v-model="selectedVehicle" filterable placeholder="Seleccione un coche" id="car"
                            @change="changeVehicle">
                            <el-option v-for="ve in vehicles" :key="ve.id"
                                :label="ve.displayName + ' -- ' + ve.licensePlate" :value="ve['@id']">
                            </el-option>
                        </el-select>
                        <small v-if="errors.vehicle" class="invalid-feedback mb-0">
                            Tienes que elegir un coche
                        </small>
                    </div>
                    <div class="col-md-6 el-col-xs-24" v-if="selectedVehicle">
                        <label class="w-100">Marca:</label>
                        <el-input v-model="vehicleBrand" disabled class="w-100">
                        </el-input>
                    </div>
                    <div class="col-md-6 el-col-xs-24 mt-2" v-if="selectedVehicle">
                        <label class="w-100">Modelo:</label>
                        <el-input v-model="vehicleModel" disabled class="w-100">
                        </el-input>
                    </div>
                    <div class="col-md-6 el-col-xs-24 mt-2" v-if="selectedVehicle">
                        <label class="w-100">Tipo de combustible:</label>
                        <el-input v-model="fuelType" disabled class="w-100">
                        </el-input>
                    </div>
                    <div class="col-md-6 el-col-xs-24 mt-2" v-if="selectedVehicle">
                        <label class="w-100">Asientos actuales:</label>
                        <el-input v-model="totalSeats" disabled class="w-100">
                        </el-input>
                    </div>
                    <div class="col-md-6 el-col-xs-24 mt-2" v-if="selectedVehicle && maxSeats > totalSeats">
                        <label class="w-100">Asientos máximos:</label>
                        <el-input v-model="maxSeats" disabled class="w-100">
                        </el-input>
                    </div>
                    <div class="col-md-6 el-col-xs-24 mt-2" v-if="selectedVehicle">
                            <label class="w-100" for="Kms">Kilómetros inicio:</label>
                            <el-input-number v-model="startKm" :min="1" :step="0.01" :precision="2" id="Kms"
                                class="w-100" disabled placeholder="Kms Inicio" />
                        </div>
                </div>
                <div class="col-md-12 row d-flex">
                    <div class="col-md-6 el-col-xs-24 mt-2">
                        <label class="w-100" for="date">Fecha y hora Inicio:</label>
                        <el-date-picker v-model="dateAndTimeStart" type="datetime" placeholder="Desde" id="date"
                            format="DD/MM/YYYY HH:mm" :clearable="false" class="mb-1 w-100"
                            :picker-options="{ firstDayOfWeek: 1 }" :disabled-date="disableDateTo"
                            @change="manageChangeFrom" />
                        <small v-if="errors.startDate" class="invalid-feedback mb-0">
                            Tienes que rellenar correctamente este campo
                        </small>
                    </div>
                    <div class="col-md-6 el-col-xs-24 mt-2">
                        <label class="w-100" for="date2">Fecha y hora Fin:</label>
                        <el-date-picker v-model="dateAndTimeEnd" type="datetime" placeholder="Desde" id="date2"
                            format="DD/MM/YYYY HH:mm" clearable class="mb-1 w-100"
                            :picker-options="dueDatePickerOptions" :disabled-date="disabledDateFrom"
                            :default-value="defaultEntDate" @change="manageChangeTo" @focus="focusDateEnd" />
                        <small v-if="errors.endDate" class="invalid-feedback mb-0">
                            Tienes que rellenar correctamente este campo
                        </small>
                    </div>
                </div>
                <div class="col-md-12 el-col-xs-24 mt-3" v-if="vehicleNeedsAdBlue">
                    <div class="alert-custom warning-adBlue">
                        <el-icon style="color: #faad14; font-size: 24px; margin-right: 8px; margin-top: -3px;">
                            <WarnTriangleFilled />
                        </el-icon>
                        <span style="font-weight: 600; font-size: 15px;">
                            Recuerda que el vehículo seleccionado requiere rellenar AdBlue en el siguiente repostaje.
                        </span>
                    </div>
                </div>
                <div class="col-md-12 el-col-xs-24 mt-3" v-if="!vehicleNeedsAdBlue && vehicleHasAdblue">
                    <div class="alert-custom warning-adBlue">
                        <el-icon style="color: #faad14; font-size: 24px; margin-right: 8px; margin-top: -3px;">
                            <WarnTriangleFilled />
                        </el-icon>
                        <span style="font-weight: 600; font-size: 15px;">
                            Recuerda que el vehículo seleccionado utiliza adBlue.
                        </span>
                    </div>
                </div>
                <div class="col-md-12 el-col-xd-24 mt-4">
                    <label class="w-100" for="purpose">Motivo:</label>
                    <textarea v-model="purpose" label="'Motivo de la solicitud'" id="purpose" rows="3"
                        @change="errors.purpose = false" :placeholder="'Motivo de la solicitud'" class="w-100 p-2">
                        </textarea>
                    <small v-if="errors.purpose" class="invalid-feedback mb-0">
                        Tienes que rellenar correctamente este campo
                    </small>
                </div>
                <div class="col-md-4 el-col-xs-24 mt-2" v-if="totalSeats < maxSeats">
                    <label class="w-100" for="seatsNeeded">¿Añadir asientos?</label>
                    <el-switch v-model="seatsNeeded" id="seatsNeeded" active-text="Sí" inactive-text="No"
                        style="margin-left: 10px;" />
                </div>
                <div class="col-md-4 el-col-xs-24 mt-2" v-if="seatsNeeded">
                    <label class="w-100" for="seats">Asientos:</label>
                    <el-input-number v-model="seatsCount" :min="1" :max="maxSeats - totalSeats" :step="1" id="seats"
                        placeholder="Número de asientos" />

                </div>
                <div class="col-md-12 el-col-xs-24 mt-2">
                    <label class="w-100" for="observations">Observaciones:</label>
                    <textarea v-model="observations" label="'Observaciones de la solicitud'" id="observations" rows="3"
                        :placeholder="'Observaciones de la solicitud'" class="w-100 p-2">
                    </textarea>
                </div>
            </div>
            <div class="row mt-4">
                <div class="col d-flex justify-content-end gap-3 dialog-actions">
                    <button class="btn btn_yellow px-4 dialog-action-button" @click="closeDialog()">
                        Cancelar
                    </button>

                    <button class="btn btn_red px-4 dialog-action-button" @click="saveRequest()">
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped lang="scss">
.my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}


.text-header {
    text-align: left;
    font-size: 4em;
    font-weight: 900;
    letter-spacing: -0.05em;
    color: #f9156f;
    margin-left: 30px;
    margin-top: -7px;
}

.iconVehicle {
    width: 40px;
    fill: $bg-blue;
}

::v-deep(.el-input-number__increase) {
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
}

::v-deep(.el-input-number__decrease) {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
}

.warning-adBlue {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #fffbe8;
    border-left: 5px solid #faad14;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 6px;
}

.dialog-content {
    margin: 0;
}

.dialog-actions {
    width: 100%;
}

.dialog-action-button {
    width: 40%;
}

@media (max-width: 767px) {
    .vehicle-new-request-dialog {
        padding: 0 2px 8px;
    }

    .my-header {
        align-items: flex-start;
        gap: 12px;
    }

    .my-header .title-text {
        margin-left: 0 !important;
        font-size: 26px;
        line-height: 1.15;
    }

    .text-header {
        font-size: 2.4em;
        margin-left: 0;
    }

    .dialog-actions {
        flex-direction: column;
    }

    .dialog-action-button {
        width: 100%;
    }
}
</style>
